/**
 * Indicator Registry & Engine
 *
 * Central catalog of all available indicators.
 * Provides calculate() to compute indicator data from candles.
 */

import { CandleData } from "../../types/chart.types";
import { IndicatorDefinition, PlotData, IndicatorCategory, ActiveIndicator } from "../types/indicator.types";

// ─── Import all built-in indicators ───────────────────────────────
// Trend
import { smaIndicator } from "../built-in/sma";
import { emaIndicator } from "../built-in/ema";
import { wmaIndicator } from "../built-in/wma";
import { demaIndicator } from "../built-in/dema";
import { temaIndicator } from "../built-in/tema";
import { hmaIndicator } from "../built-in/hma";
import { vwmaIndicator } from "../built-in/vwma";
import { vwapIndicator } from "../built-in/vwap";
import { bollingerIndicator } from "../built-in/bollinger";
import { ichimokuIndicator } from "../built-in/ichimoku";
import { parabolicSarIndicator } from "../built-in/parabolic-sar";
import { supertrendIndicator } from "../built-in/supertrend";
import { aroonIndicator } from "../built-in/aroon";
import { kamaIndicator } from "../built-in/kama";
import { smmaIndicator } from "../built-in/smma";
import { vidyaIndicator } from "../built-in/vidya";
import { mcginleyIndicator } from "../built-in/mcginley";
import { linregIndicator } from "../built-in/linreg";
import { krollStopIndicator } from "../built-in/kroll-stop";
import { rangeFilterIndicator } from "../built-in/range-filter";
import { hmaSignalsIndicator } from "../built-in/hma-signals";
import { lrChannelIndicator } from "../built-in/lr-channel";
import { efficiencyRatioIndicator } from "../built-in/efficiency-ratio";
import { trendQualityIndicator } from "../built-in/trend-quality";

// Momentum
import { rsiIndicator } from "../built-in/rsi";
import { macdIndicator } from "../built-in/macd";
import { stochasticIndicator } from "../built-in/stochastic";
import { cciIndicator } from "../built-in/cci";
import { williamsRIndicator } from "../built-in/williams-r";
import { adxIndicator } from "../built-in/adx";
import { mfiIndicator } from "../built-in/mfi";
import { rocIndicator } from "../built-in/roc";
import { stochRsiIndicator } from "../built-in/stoch-rsi";
import { momentumIndicator } from "../built-in/momentum";
import { tsiIndicator } from "../built-in/tsi";
import { awesomeOscillatorIndicator } from "../built-in/awesome-oscillator";
import { cmoIndicator } from "../built-in/cmo";
import { ultimateOscIndicator } from "../built-in/ultimate-oscillator";
import { forceIndexIndicator } from "../built-in/force-index";
import { dmiIndicator } from "../built-in/dmi";
import { coppockIndicator } from "../built-in/coppock";
import { massIndexIndicator } from "../built-in/mass-index";
import { trixIndicator } from "../built-in/trix";
import { kstIndicator } from "../built-in/kst";
import { elderRayIndicator } from "../built-in/elder-ray";
import { zScoreIndicator } from "../built-in/z-score";

// Volatility
import { atrIndicator } from "../built-in/atr";
import { donchianIndicator } from "../built-in/donchian";
import { keltnerIndicator } from "../built-in/keltner";
import { stddevIndicator } from "../built-in/stddev";
import { bbwIndicator } from "../built-in/bbw";
import { chandelierIndicator } from "../built-in/chandelier";
import { rviIndicator } from "../built-in/rvi";
import { historicalVolIndicator } from "../built-in/historical-volatility";
import { choppinessIndicator } from "../built-in/choppiness";
import { natrIndicator } from "../built-in/natr";
import { adrIndicator } from "../built-in/adr";
import { volatilityRatioIndicator } from "../built-in/volatility-ratio";
import { bbSqueezeIndicator } from "../built-in/bb-squeeze";

// Volume
import { volumeIndicator } from "../built-in/volume";
import { obvIndicator } from "../built-in/obv";
import { adlIndicator } from "../built-in/adl";
import { chaikinIndicator } from "../built-in/chaikin";
import { vptIndicator } from "../built-in/vpt";
import { klingerIndicator } from "../built-in/klinger";
import { emvIndicator } from "../built-in/emv";
import { vrocIndicator } from "../built-in/vroc";
import { volumeDeltaIndicator } from "../built-in/volume-delta";

// Support / Resistance
import { pivotPointsIndicator } from "../built-in/pivot-points";
import { fibPivotIndicator } from "../built-in/fib-pivot";
import { woodiePivotIndicator } from "../built-in/woodie-pivot";
import { camarillaPivotIndicator } from "../built-in/camarilla-pivot";
import { demarkPivotIndicator } from "../built-in/demark-pivot";
import { zigzagIndicator } from "../built-in/zigzag";
import { fractalsIndicator } from "../built-in/fractals";
import { fractalLevelsIndicator } from "../built-in/fractal-levels";
import { pivotConfluenceIndicator } from "../built-in/pivot-confluence";

// Advanced
import { alligatorIndicator } from "../built-in/alligator";
import { acceleratorIndicator } from "../built-in/accelerator";
import { gatorIndicator } from "../built-in/gator";
import { squeezeIndicator } from "../built-in/squeeze";
import { fvgIndicator } from "../built-in/fair-value-gap";
import { orderBlocksIndicator } from "../built-in/order-blocks";
import { liquidityPoolsIndicator } from "../built-in/liquidity-pools";
import { rsiDivergenceIndicator } from "../built-in/rsi-divergence";
import { macdDivergenceIndicator } from "../built-in/macd-divergence";
import { smaCrossoverIndicator } from "../built-in/sma-crossover";
import { rsiReversalIndicator } from "../built-in/rsi-reversal";
import { marketRegimeIndicator } from "../built-in/market-regime";
import { hurstExponentIndicator } from "../built-in/hurst-exponent";
import { mtfEmaIndicator } from "../built-in/mtf-ema";
import { mtfRsiIndicator } from "../built-in/mtf-rsi";

// Sessions
import { asiaSessionIndicator, londonSessionIndicator, nySessionIndicator, killzoneIndicator } from "../built-in/sessions";
import { orbIndicator } from "../built-in/opening-range-breakout";

// Strategies
import { alphaTradeIndicator } from "../built-in/alpha-trade";
import { profitMachineIndicator } from "../built-in/profit-machine";
import { scalpingTrendIndicator } from "../built-in/scalping-trend";
import { primeMagicIndicator } from "../built-in/prime-magic";
import { maxWealthIndicator } from "../built-in/max-wealth";
import { swingTradeIndicator } from "../built-in/swing-trade";
import { trendRoverIndicator } from "../built-in/trend-rover";
import { trendMonsterIndicator } from "../built-in/trend-monster";
import { tradeMintIndicator } from "../built-in/trade-mint";
import { priceActionIndicator } from "../built-in/price-action";
import { alphaTradeV2Indicator } from "../built-in/alpha-trade-v2";
import { shadowBreakerIndicator } from "../built-in/shadow-breaker";

class IndicatorEngine {
  private registry: Map<string, IndicatorDefinition> = new Map();

  constructor() {
    // Register all built-in indicators
    const builtIns: IndicatorDefinition[] = [
      // ─── Trend ───
      smaIndicator,
      emaIndicator,
      wmaIndicator,
      demaIndicator,
      temaIndicator,
      hmaIndicator,
      vwmaIndicator,
      vwapIndicator,
      bollingerIndicator,
      ichimokuIndicator,
      parabolicSarIndicator,
      supertrendIndicator,
      aroonIndicator,
      kamaIndicator,
      smmaIndicator,
      vidyaIndicator,
      mcginleyIndicator,
      linregIndicator,
      krollStopIndicator,
      rangeFilterIndicator,
      hmaSignalsIndicator,
      lrChannelIndicator,
      efficiencyRatioIndicator,
      trendQualityIndicator,

      // ─── Momentum ───
      rsiIndicator,
      macdIndicator,
      stochasticIndicator,
      cciIndicator,
      williamsRIndicator,
      adxIndicator,
      mfiIndicator,
      rocIndicator,
      stochRsiIndicator,
      momentumIndicator,
      tsiIndicator,
      awesomeOscillatorIndicator,
      cmoIndicator,
      ultimateOscIndicator,
      forceIndexIndicator,
      dmiIndicator,
      coppockIndicator,
      massIndexIndicator,
      trixIndicator,
      kstIndicator,
      elderRayIndicator,
      zScoreIndicator,

      // ─── Volatility ───
      atrIndicator,
      donchianIndicator,
      keltnerIndicator,
      stddevIndicator,
      bbwIndicator,
      chandelierIndicator,
      rviIndicator,
      historicalVolIndicator,
      choppinessIndicator,
      natrIndicator,
      adrIndicator,
      volatilityRatioIndicator,
      bbSqueezeIndicator,

      // ─── Volume ───
      volumeIndicator,
      obvIndicator,
      adlIndicator,
      chaikinIndicator,
      vptIndicator,
      klingerIndicator,
      emvIndicator,
      vrocIndicator,
      volumeDeltaIndicator,

      // ─── Support / Resistance ───
      pivotPointsIndicator,
      fibPivotIndicator,
      woodiePivotIndicator,
      camarillaPivotIndicator,
      demarkPivotIndicator,
      zigzagIndicator,
      fractalsIndicator,
      fractalLevelsIndicator,
      pivotConfluenceIndicator,

      // ─── Advanced ───
      alligatorIndicator,
      acceleratorIndicator,
      gatorIndicator,
      squeezeIndicator,
      fvgIndicator,
      orderBlocksIndicator,
      liquidityPoolsIndicator,
      rsiDivergenceIndicator,
      macdDivergenceIndicator,
      smaCrossoverIndicator,
      rsiReversalIndicator,
      marketRegimeIndicator,
      hurstExponentIndicator,
      mtfEmaIndicator,
      mtfRsiIndicator,

      // ─── Sessions ───
      asiaSessionIndicator,
      londonSessionIndicator,
      nySessionIndicator,
      killzoneIndicator,
      orbIndicator,

      // ─── Strategies ───
      alphaTradeIndicator,
      profitMachineIndicator,
      scalpingTrendIndicator,
      primeMagicIndicator,
      maxWealthIndicator,
      shadowBreakerIndicator,
      swingTradeIndicator,
      trendRoverIndicator,
      trendMonsterIndicator,
      tradeMintIndicator,
      priceActionIndicator,
      alphaTradeV2Indicator,
    ];

    builtIns.forEach(ind => this.registry.set(ind.id, ind));
  }

  /** Get all available indicators */
  getAll(): IndicatorDefinition[] {
    return Array.from(this.registry.values());
  }

  /** Get indicators by category */
  getByCategory(category: IndicatorCategory): IndicatorDefinition[] {
    return this.getAll().filter(ind => ind.category === category);
  }

  /** Get a single indicator definition */
  get(id: string): IndicatorDefinition | undefined {
    return this.registry.get(id);
  }

  /** Register a custom indicator dynamically */
  register(def: IndicatorDefinition): void {
    this.registry.set(def.id, def);
  }

  /** Unregister a custom indicator */
  unregister(id: string): boolean {
    return this.registry.delete(id);
  }

  /** Calculate indicator data */
  calculate(indicatorId: string, candles: CandleData[], params: Record<string, any>): PlotData[] {
    const def = this.registry.get(indicatorId);
    if (!def) {
      console.warn(`[Indicator] Unknown indicator: ${indicatorId}`);
      return [];
    }
    try {
      return def.calculate(candles, params);
    } catch (error) {
      console.error(`[Indicator] Error calculating ${indicatorId}:`, error);
      return [];
    }
  }

  /** Create default params for an indicator */
  getDefaultParams(indicatorId: string): Record<string, any> {
    const def = this.registry.get(indicatorId);
    if (!def) return {};
    const params: Record<string, any> = {};
    def.params.forEach(p => { params[p.key] = p.default; });
    return params;
  }

  /** Create a new ActiveIndicator instance */
  createInstance(indicatorId: string): ActiveIndicator | null {
    const def = this.registry.get(indicatorId);
    if (!def) return null;
    return {
      instanceId: `${indicatorId}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      indicatorId,
      params: this.getDefaultParams(indicatorId),
      visible: true,
    };
  }
}

export const indicatorEngine = new IndicatorEngine();
